// Profile module type definitions

export interface UserProfile {
  userId: string;
  // Required fields (from onboarding)
  name: string;
  profession: string;
  industry: string;
  city: string;
  
  // Optional personal fields
  profilePhotoUrl?: string;
  previewImageUrl?: string; // Gold card preview for sharing
  bio?: string;
  skills?: string[];
  experienceLevel?: 'Beginner' | 'Intermediate' | 'Expert';
  languages?: string[];
  availability?: 'Full-time' | 'Part-time' | 'Freelance';
  website?: string;
  
  // Business reference
  businessId?: string;
}

export interface BusinessProfile {
  businessId: string;
  ownerId: string;
  
  // Required fields
  name: string;
  category: string;
  description: string;
  
  // Optional fields
  logoUrl?: string;
  city?: string;
  yearsOfExperience?: number;
  website?: string;
  contactEmail?: string;
}

// Form data types for editing
export interface PersonalDetailsFormData {
  name: string;
  profession: string;
  industry: string;
  city: string;
  bio?: string;
  skills?: string[];
  experienceLevel?: 'Beginner' | 'Intermediate' | 'Expert';
  languages?: string[];
  availability?: 'Full-time' | 'Part-time' | 'Freelance';
  website?: string;
}

export interface BusinessFormData {
  name: string;
  category: string;
  description: string;
  city?: string;
  yearsOfExperience?: number;
  website?: string;
  contactEmail?: string;
}

// Business categories
export const BUSINESS_CATEGORIES = [
  'Technology',
  'Marketing',
  'Design',
  'Consulting',
  'Finance',
  'Healthcare',
  'Education',
  'Real Estate',
  'Retail',
  'Manufacturing',
  'Construction',
  'Food & Beverage',
  'Transportation',
  'Entertainment',
  'Other',
] as const;

// Industries
export const INDUSTRIES = [
  // Technology & Digital
  'Technology',
  'Software Development',
  'IT Services',
  'Cybersecurity',
  'Artificial Intelligence',
  'Data Science & Analytics',
  'Cloud Computing',
  'Telecommunications',
  'Electronics & Hardware',
  'E-Commerce',

  // Finance & Business
  'Finance',
  'Banking',
  'Insurance',
  'Accounting & Auditing',
  'Investment & Wealth Management',
  'Taxation',
  'Consulting',
  'Business Development',
  'Human Resources',
  'Recruitment & Staffing',

  // Construction, Architecture & Design
  'Architecture',
  'Interior Design',
  'Landscape Architecture',
  'Urban Planning',
  'Construction',
  'Civil Engineering',
  'Structural Engineering',
  'Electrical Engineering',
  'Mechanical Engineering',
  'Renovation & Remodeling',
  'Flooring & Tiling',
  'Painting & Finishing',
  'Plumbing',
  'HVAC',
  'Carpentry & Woodwork',
  'Modular & Prefab Construction',
  'Green Building & Sustainability',

  // Real Estate & Property
  'Real Estate',
  'Property Management',
  'Real Estate Development',
  'Commercial Real Estate',
  'Residential Real Estate',
  'Co-working Spaces',

  // Healthcare & Wellness
  'Healthcare',
  'Hospital & Clinic Management',
  'Pharmaceuticals',
  'Medical Devices',
  'Mental Health',
  'Nutrition & Dietetics',
  'Fitness & Wellness',
  'Yoga & Meditation',
  'Alternative Medicine',
  'Veterinary Services',
  'Elder Care',

  // Education & Training
  'Education',
  'EdTech',
  'Coaching & Tutoring',
  'Vocational Training',
  'Corporate Training',
  'Early Childhood Education',

  // Retail & Consumer
  'Retail',
  'Fashion & Apparel',
  'Jewellery',
  'Beauty & Cosmetics',
  'Luxury Goods',
  'Consumer Electronics',
  'Home Decor & Furnishing',
  'Sports & Outdoor',
  'Toys & Games',
  'Grocery & Supermarkets',

  // Food, Beverage & Hospitality
  'Food & Beverage',
  'Restaurant & Dining',
  'Cloud Kitchen',
  'Catering',
  'Bakery & Confectionery',
  'Food Processing',
  'Beverages & Drinks',
  'Hotels & Hospitality',
  'Travel & Tourism',
  'Event Management',
  'Wedding Planning',

  // Manufacturing & Industrial
  'Manufacturing',
  'Automotive',
  'Aerospace & Defence',
  'Textiles & Garments',
  'Chemical & Petrochemical',
  'Steel & Metals',
  'Packaging',
  'Printing & Publishing',
  'Agricultural Equipment',

  // Agriculture & Environment
  'Agriculture & Farming',
  'Horticulture & Nursery',
  'Fisheries & Aquaculture',
  'Forestry',
  'Renewable Energy',
  'Solar Energy',
  'Environmental Services',
  'Waste Management',
  'Water Management',

  // Media, Arts & Creative
  'Marketing',
  'Advertising & PR',
  'Media',
  'Journalism',
  'Film & Video Production',
  'Photography',
  'Graphic Design',
  'Design',
  'Animation & VFX',
  'Music & Audio',
  'Performing Arts',
  'Fine Arts & Crafts',
  'Gaming & Esports',

  // Logistics & Transport
  'Transportation',
  'Logistics & Supply Chain',
  'Shipping & Freight',
  'Aviation',
  'Railways',
  'Last-Mile Delivery',
  'Fleet Management',

  // Legal, Govt & Public Services
  'Legal',
  'Government & Public Sector',
  'Non-Profit & NGO',
  'Social Work',
  'Political & Policy',

  // Professional & Personal Services
  'Research & Development',
  'Clinical Research',
  'Market Research',
  'Security & Surveillance',
  'Cleaning & Facility Services',
  'Pest Control',
  'Pet Services',
  'Personal Care',
  'Childcare',
  'Astrology & Vastu',

  'Other',
] as const;
